import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Copy, GripVertical, ImageIcon, Plus, Quote, Trash2, Type } from 'lucide-react';
import { WpMediaPickerModal } from '../../components/WpMediaPickerModal';
import type { WpMediaItem } from '../../lib/api';
import {
  CUSTOMER_AVATAR_AGES,
  CUSTOMER_AVATAR_COUNT,
  getCustomerAvatarUrl,
  normalizeCustomerAvatarAge,
  normalizeCustomerAvatarGender,
  normalizeCustomerAvatarIndex,
  type CustomerAvatarAge,
  type CustomerAvatarGender,
} from './customer-avatar-presets';
import { normalizeHexColor, prefixHexColor, safeColorForPicker } from './builder-v2/utils/color-utils';

export const PRODUCT_ADDITIONAL_ELEMENTS_META_KEY = '_ef_product_additional_elements';
export const MAX_PRODUCT_ADDITIONAL_ELEMENTS = 5;

const CANVAS_ID = '__product_additional_elements_canvas__';

export type ProductAdditionalElementType = 'customBox' | 'review' | 'image';

interface ProductAdditionalElementBase {
  id: string;
  type: ProductAdditionalElementType;
  marginTop: number;
}

export interface ProductCustomBoxElement extends ProductAdditionalElementBase {
  type: 'customBox';
  text: string;
  textColor: string;
  backgroundColor: string;
  borderColor: string;
}

export interface ProductReviewElement extends ProductAdditionalElementBase {
  type: 'review';
  authorName: string;
  countryCode: string;
  avatarType: 'preset' | 'custom';
  avatarCustom: string;
  avatarPresetGender: CustomerAvatarGender;
  avatarPresetAge: CustomerAvatarAge;
  avatarPresetIndex: number;
  text: string;
  rating: number;
  aggregateRating: string;
  aggregateLabel: string;
  reviewCount: string;
}

export interface ProductImageElement extends ProductAdditionalElementBase {
  type: 'image';
  imageUrl: string;
  alt: string;
  linkUrl: string;
}

export type ProductAdditionalElement =
  | ProductCustomBoxElement
  | ProductReviewElement
  | ProductImageElement;

type ProductAdditionalElementPatch =
  | Partial<ProductCustomBoxElement>
  | Partial<ProductReviewElement>
  | Partial<ProductImageElement>;

interface ProductAdditionalElementsSectionProps {
  integrationId?: string;
  value: ProductAdditionalElement[];
  onChange: (elements: ProductAdditionalElement[]) => void;
  description?: string;
  mediaPickerEnabled?: boolean;
}

const PALETTE: Array<{
  type: ProductAdditionalElementType;
  label: string;
  description: string;
  icon: typeof Type;
}> = [
  {
    type: 'customBox',
    label: 'Custom box',
    description: 'Krótki komunikat pod przyciskiem zakupu',
    icon: Type,
  },
  {
    type: 'review',
    label: 'Opinia',
    description: 'Pojedyncza opinia z avatarem i oceną',
    icon: Quote,
  },
  {
    type: 'image',
    label: 'Grafika',
    description: 'Baner lub grafika z biblioteki mediów',
    icon: ImageIcon,
  },
];

const DEFAULT_REVIEW_TEXT = 'To jest przykładowa opinia klienta o produkcie. Umieszczona w tym miejscu wzmacnia wiarygodność oferty i pomaga szybciej podjąć decyzję o zakupie.';

const COUNTRY_OPTIONS = [
  { code: 'pl', label: 'Polska' },
  { code: 'cz', label: 'Czechy' },
  { code: 'sk', label: 'Słowacja' },
  { code: 'de', label: 'Niemcy' },
  { code: 'at', label: 'Austria' },
  { code: 'nl', label: 'Holandia' },
  { code: 'fr', label: 'Francja' },
  { code: 'it', label: 'Włochy' },
  { code: 'es', label: 'Hiszpania' },
  { code: 'ro', label: 'Rumunia' },
  { code: 'hu', label: 'Węgry' },
  { code: 'gb', label: 'Wielka Brytania' },
  { code: 'us', label: 'USA' },
  { code: 'se', label: 'Szwecja' },
  { code: 'dk', label: 'Dania' },
];
function createElementId() {
  return `product-extra-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function createDefaultElement(type: ProductAdditionalElementType): ProductAdditionalElement {
  if (type === 'customBox') {
    return {
      id: createElementId(),
      type,
      marginTop: 10,
      text: '🇵🇱 Darmowa dostawa w 24h z InPost 🌙',
      textColor: '#1e1e1e',
      backgroundColor: '#ffffff',
      borderColor: '#ededed',
    };
  }

  if (type === 'review') {
    return {
      id: createElementId(),
      type,
      marginTop: 10,
      authorName: 'Ewa Czajka',
      countryCode: 'pl',
      avatarType: 'preset',
      avatarCustom: '',
      avatarPresetGender: 'female',
      avatarPresetAge: '50-60',
      avatarPresetIndex: 3,
      text: DEFAULT_REVIEW_TEXT,
      rating: 5,
      aggregateRating: '4.8',
      aggregateLabel: 'Znakomity',
      reviewCount: '5,456',
    };
  }

  return {
    id: createElementId(),
    type,
    marginTop: 10,
    imageUrl: '',
    alt: 'Grafika pod przyciskiem zakupu',
    linkUrl: '',
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asString(value: unknown, fallback = '') {
  return typeof value === 'string' ? value : fallback;
}

function asColor(value: unknown, fallback: string) {
  const text = asString(value, fallback);
  return /^#[0-9a-f]{6}$/i.test(text) ? text : fallback;
}

function asNumber(value: unknown, fallback: number, min: number, max: number) {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

function normalizeCountryCode(value: unknown) {
  const code = asString(value, 'pl').trim().toLowerCase().replace(/[^a-z]/g, '').slice(0, 2);
  return code || 'pl';
}

function getCountryLabel(code: string) {
  return COUNTRY_OPTIONS.find((country) => country.code === code)?.label ?? code.toUpperCase();
}

function normalizeAdditionalElement(value: unknown): ProductAdditionalElement | null {
  if (!isRecord(value)) return null;

  const type = value.type;
  const id = asString(value.id, createElementId());

  if (type === 'customBox') {
    return {
      id,
      type,
      marginTop: asNumber(value.marginTop, 10, 0, 120),
      text: asString(value.text, '🇵🇱 Darmowa dostawa w 24h z InPost 🌙'),
      textColor: asColor(value.textColor, '#1e1e1e'),
      backgroundColor: asColor(value.backgroundColor, '#ffffff'),
      borderColor: asColor(value.borderColor, '#ededed'),
    };
  }

  if (type === 'review') {
    return {
      id,
      type,
      marginTop: asNumber(value.marginTop, 10, 0, 120),
      authorName: asString(value.authorName, 'Ewa Czajka'),
      countryCode: normalizeCountryCode(value.countryCode),
      avatarType: value.avatarType === 'custom' ? 'custom' : 'preset',
      avatarCustom: asString(value.avatarCustom),
      avatarPresetGender: normalizeCustomerAvatarGender(value.avatarPresetGender),
      avatarPresetAge: normalizeCustomerAvatarAge(value.avatarPresetAge),
      avatarPresetIndex: normalizeCustomerAvatarIndex(value.avatarPresetIndex, 3),
      text: asString(value.text, DEFAULT_REVIEW_TEXT),
      rating: asNumber(value.rating, 5, 0, 5),
      aggregateRating: asString(value.aggregateRating, '4.8'),
      aggregateLabel: asString(value.aggregateLabel, 'Znakomity'),
      reviewCount: asString(value.reviewCount, '5,456'),
    };
  }

  if (type === 'image') {
    return {
      id,
      type,
      marginTop: asNumber(value.marginTop, 10, 0, 120),
      imageUrl: asString(value.imageUrl),
      alt: asString(value.alt, 'Grafika pod przyciskiem zakupu'),
      linkUrl: asString(value.linkUrl),
    };
  }

  return null;
}

export function normalizeProductAdditionalElements(value: unknown): ProductAdditionalElement[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(normalizeAdditionalElement)
    .filter((element): element is ProductAdditionalElement => element !== null)
    .slice(0, MAX_PRODUCT_ADDITIONAL_ELEMENTS);
}

export function parseProductAdditionalElementsMeta(
  metaData: Array<{ key: string; value: unknown }> | undefined,
): ProductAdditionalElement[] {
  let raw: unknown;
  if (metaData) {
    for (let index = metaData.length - 1; index >= 0; index -= 1) {
      if (metaData[index].key === PRODUCT_ADDITIONAL_ELEMENTS_META_KEY) {
        raw = metaData[index].value;
        break;
      }
    }
  }
  if (!raw) return [];

  if (Array.isArray(raw)) {
    return normalizeProductAdditionalElements(raw);
  }

  if (typeof raw !== 'string') return [];

  try {
    return normalizeProductAdditionalElements(JSON.parse(raw));
  } catch {
    return [];
  }
}

export function serializeProductAdditionalElements(elements: ProductAdditionalElement[]) {
  return JSON.stringify(normalizeProductAdditionalElements(elements));
}

function getElementTitle(element: ProductAdditionalElement) {
  if (element.type === 'customBox') return 'Custom box';
  if (element.type === 'review') return `Opinia - ${element.authorName || 'autor'}`;
  return 'Grafika';
}

function renderStars(rating: number) {
  const count = Math.min(5, Math.max(0, Math.round(rating)));
  return (
    <span className="inline-flex items-center gap-0.5 rounded border border-border-strong px-1 py-0.5 leading-none">
      {Array.from({ length: 5 }, (_, index) => (
        <span
          key={index}
          className={`text-[10px] leading-none ${index < count ? 'text-warning' : 'text-text-disabled'}`}
        >
          ★
        </span>
      ))}
    </span>
  );
}

function AvatarPreview({ element, size = 'md' }: { element: ProductReviewElement; size?: 'sm' | 'md' }) {
  const initials = element.authorName
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'EF';
  const dimensions = size === 'sm' ? 'w-8 h-8 text-[10px]' : 'w-12 h-12 text-sm';

  if (element.avatarType === 'custom' && element.avatarCustom) {
    return (
      <img
        src={element.avatarCustom}
        alt={element.authorName}
        className={`${dimensions} shrink-0 aspect-square rounded-full object-cover border border-border-subtle`}
      />
    );
  }

  const avatarUrl = getCustomerAvatarUrl(
    element.avatarPresetGender,
    element.avatarPresetAge,
    element.avatarPresetIndex,
  );

  return (
    <img
      src={avatarUrl}
      alt={element.authorName || initials}
      className={`${dimensions} shrink-0 aspect-square rounded-full object-cover border border-border-subtle`}
    />
  );
}

function AdditionalElementPreview({ element }: { element: ProductAdditionalElement }) {
  if (element.type === 'customBox') {
    return (
      <div
        className="w-full rounded-lg border px-4 py-3 text-center text-sm font-medium"
        style={{
          color: element.textColor,
          backgroundColor: element.backgroundColor,
          borderColor: element.borderColor,
        }}
      >
        {element.text || 'Custom box'}
      </div>
    );
  }

  if (element.type === 'review') {
    return (
      <div className="rounded-lg border border-border-subtle bg-surface-base/60 p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <AvatarPreview element={element} size="sm" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-text-primary truncate">{element.authorName || 'Autor opinii'}</p>
              <div className="flex items-center gap-0.5 mt-0.5">{renderStars(element.rating)}</div>
            </div>
          </div>
          <span className="text-[10px] uppercase text-text-muted">{getCountryLabel(element.countryCode)}</span>
        </div>
        <p className="text-xs text-text-secondary leading-relaxed mt-2 line-clamp-2">{element.text || 'Treść opinii'}</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border-subtle bg-surface-base/60 overflow-hidden">
      {element.imageUrl ? (
        <img src={element.imageUrl} alt={element.alt} className="w-full max-h-28 object-cover" />
      ) : (
        <div className="h-24 flex items-center justify-center text-text-muted">
          <ImageIcon className="w-6 h-6" />
        </div>
      )}
    </div>
  );
}

function PaletteItem({
  item,
  disabled,
  onAdd,
}: {
  item: (typeof PALETTE)[number];
  disabled: boolean;
  onAdd: () => void;
}) {
  const Icon = item.icon;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `palette-${item.type}`,
    data: { source: 'palette', type: item.type },
    disabled,
  });
  const style = {
    transform: CSS.Translate.toString(transform),
  };

  return (
    <button
      ref={setNodeRef}
      type="button"
      disabled={disabled}
      onClick={onAdd}
      style={style}
      {...attributes}
      {...listeners}
      className={`w-full text-left rounded-xl border p-3 transition-all ${
        disabled
          ? 'border-border-subtle bg-surface-base/40 opacity-50 cursor-not-allowed'
          : 'border-border bg-surface-raised/40 hover:border-accent-primary/40 hover:bg-accent-primary/5 cursor-grab active:cursor-grabbing'
      } ${isDragging ? 'opacity-40' : ''}`}
    >
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 rounded-lg bg-accent-primary/10 text-accent-primary flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4" />
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-text-primary">{item.label}</span>
          <span className="block text-[11px] leading-snug text-text-muted">{item.description}</span>
        </span>
      </div>
    </button>
  );
}

function CanvasDropZone({
  children,
  isEmpty,
  isFull,
  isDragActive,
}: {
  children: ReactNode;
  isEmpty: boolean;
  isFull: boolean;
  isDragActive: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: CANVAS_ID });
  const isDropReady = isDragActive && !isFull;
  return (
    <div
      ref={setNodeRef}
      className={`min-h-[220px] rounded-xl border-2 border-dashed p-3 transition-all ${
        isOver && !isFull
          ? 'border-accent-primary bg-accent-primary/5'
          : isDropReady
            ? 'border-accent-primary/50 bg-accent-primary/[0.03] ring-1 ring-accent-primary/20'
            : 'border-border-subtle bg-surface-base/40'
      }`}
    >
      {isEmpty ? (
        <div className="h-full min-h-[190px] flex flex-col items-center justify-center text-center text-text-muted">
          <Plus className="w-7 h-7 mb-2 text-accent-primary/60" />
          <p className="text-sm font-medium text-text-secondary">Brak dodatkowych elementów</p>
          <p className="text-xs mt-1">Dodaj komponent z listy obok</p>
        </div>
      ) : (
        children
      )}
    </div>
  );
}

function SortableElementCard({
  element,
  selected,
  canDuplicate,
  onSelect,
  onDuplicate,
  onRemove,
}: {
  element: ProductAdditionalElement;
  selected: boolean;
  canDuplicate: boolean;
  onSelect: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: element.id,
    data: { source: 'canvas' },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group rounded-xl border bg-surface-raised/60 p-3 transition-all ${
        selected ? 'border-accent-primary ring-2 ring-accent-primary/20' : 'border-border hover:border-border-strong'
      } ${isDragging ? 'opacity-50' : ''}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <button type="button" onClick={onSelect} className="min-w-0 flex-1 text-left">
          <span className="block text-xs font-semibold text-text-primary truncate">{getElementTitle(element)}</span>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-hover cursor-grab active:cursor-grabbing"
            title="Przeciągnij"
          >
            <GripVertical className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onDuplicate();
            }}
            disabled={!canDuplicate}
            className="p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-hover disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-text-muted"
            title={canDuplicate ? 'Duplikuj' : 'Limit elementów'}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="p-1.5 rounded-lg text-text-muted hover:text-danger hover:bg-danger/10"
            title="Usuń"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <button type="button" onClick={onSelect} className="w-full text-left">
        <AdditionalElementPreview element={element} />
      </button>
    </div>
  );
}

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return <label htmlFor={htmlFor} className="block text-xs text-text-secondary mb-1.5">{children}</label>;
}

function TextInput({
  id,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <input
      id={id}
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="input w-full text-sm"
      placeholder={placeholder}
    />
  );
}

function ColorField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const isValidHex = (color: string) => /^#[0-9a-f]{6}$/i.test(color);

  const commitTextColor = (next: string) => {
    const prefixed = prefixHexColor(next);
    setDraft(prefixed);
    const normalized = normalizeHexColor(prefixed);
    if (isValidHex(normalized)) {
      onChange(normalized);
    }
  };

  const handleTextBlur = () => {
    const normalized = normalizeHexColor(draft);
    if (isValidHex(normalized)) {
      setDraft(normalized);
      onChange(normalized);
      return;
    }
    setDraft(value);
  };

  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="color"
          value={safeColorForPicker(value)}
          onChange={(event) => {
            setDraft(event.target.value);
            onChange(event.target.value);
          }}
          className="h-10 w-12 rounded-lg border border-border bg-surface-hover p-1"
        />
        <input
          type="text"
          value={draft}
          onChange={(event) => commitTextColor(event.target.value)}
          onBlur={handleTextBlur}
          className="input w-full text-sm font-mono"
          placeholder="#ededed"
        />
      </div>
    </div>
  );
}

function NumberField({
  id,
  label,
  value,
  min,
  max,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commitDraft = (nextDraft: string) => {
    const numeric = Number(nextDraft);
    if (!Number.isFinite(numeric)) {
      setDraft(String(value));
      return;
    }

    const nextValue = Math.min(max, Math.max(min, Math.round(numeric)));
    onChange(nextValue);
    setDraft(String(nextValue));
  };

  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <input
        id={id}
        type="number"
        min={min}
        max={max}
        value={draft}
        onChange={(event) => {
          const nextDraft = event.target.value;
          setDraft(nextDraft);
          if (nextDraft === '') return;

          const numeric = Number(nextDraft);
          if (Number.isFinite(numeric) && numeric >= min && numeric <= max) {
            onChange(Math.round(numeric));
          }
        }}
        onBlur={() => {
          if (draft === '') {
            setDraft(String(value));
            return;
          }
          commitDraft(draft);
        }}
        className="input w-full text-sm"
      />
    </div>
  );
}

function AvatarPresetPicker({
  value,
  gender,
  age,
  onChange,
}: {
  value: number;
  gender: CustomerAvatarGender;
  age: CustomerAvatarAge;
  onChange: (value: number) => void;
}) {
  return (
    <div className="grid grid-cols-10 gap-1.5">
      {Array.from({ length: CUSTOMER_AVATAR_COUNT }, (_, index) => {
        const avatarIndex = index + 1;
        const selected = value === avatarIndex;
        const avatarUrl = getCustomerAvatarUrl(gender, age, avatarIndex);
        return (
          <button
            key={avatarIndex}
            type="button"
            onClick={() => onChange(avatarIndex)}
            className={`w-8 h-8 min-w-[2rem] shrink-0 aspect-square overflow-hidden rounded-full border-2 transition-all ${
              selected
                ? 'border-accent-primary ring-2 ring-accent-primary/30 scale-105'
                : 'border-transparent hover:border-border-strong'
            }`}
            title={`Avatar ${avatarIndex}`}
          >
            <img src={avatarUrl} alt={`Avatar ${avatarIndex}`} className="h-full w-full object-cover" />
          </button>
        );
      })}
    </div>
  );
}

function ElementSpacingField({
  element,
  onUpdate,
}: {
  element: ProductAdditionalElement;
  onUpdate: (patch: ProductAdditionalElementPatch) => void;
}) {
  const id = `product-extra-${element.id}-marginTop`;

  return (
    <div className="rounded-xl border border-border-subtle bg-surface-base/40 p-3">
      <NumberField
        id={id}
        label="Górny margines od poprzedniego elementu (px)"
        min={0}
        max={120}
        value={element.marginTop}
        onChange={(marginTop) => onUpdate({ marginTop })}
      />
    </div>
  );
}

function ElementConfigPanel({
  element,
  onUpdate,
  onPickImage,
  onRemoveImage,
  mediaPickerEnabled,
}: {
  element: ProductAdditionalElement;
  onUpdate: (patch: ProductAdditionalElementPatch) => void;
  onPickImage: (field: 'imageUrl' | 'avatarCustom') => void;
  onRemoveImage: (field: 'imageUrl' | 'avatarCustom') => void;
  mediaPickerEnabled: boolean;
}) {
  const fieldId = (name: string) => `product-extra-${element.id}-${name}`;

  if (element.type === 'customBox') {
    return (
      <div className="space-y-4">
        <ElementSpacingField element={element} onUpdate={onUpdate} />
        <div>
          <FieldLabel htmlFor={fieldId('text')}>Tekst</FieldLabel>
          <textarea
            id={fieldId('text')}
            value={element.text}
            onChange={(event) => onUpdate({ text: event.target.value })}
            className="input w-full text-sm"
            rows={3}
            placeholder="Tekst w custom boxie"
          />
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <ColorField id={fieldId('textColor')} label="Tekst" value={element.textColor} onChange={(textColor) => onUpdate({ textColor })} />
          <ColorField id={fieldId('backgroundColor')} label="Tło" value={element.backgroundColor} onChange={(backgroundColor) => onUpdate({ backgroundColor })} />
          <ColorField id={fieldId('borderColor')} label="Obramowanie" value={element.borderColor} onChange={(borderColor) => onUpdate({ borderColor })} />
        </div>
      </div>
    );
  }

  if (element.type === 'review') {
    return (
      <div className="space-y-4">
        <ElementSpacingField element={element} onUpdate={onUpdate} />
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <FieldLabel htmlFor={fieldId('authorName')}>Autor</FieldLabel>
            <TextInput id={fieldId('authorName')} value={element.authorName} onChange={(authorName) => onUpdate({ authorName })} placeholder="Imię i nazwisko" />
          </div>
          <div>
            <FieldLabel htmlFor={fieldId('countryCode')}>Kraj autora</FieldLabel>
            <select
              id={fieldId('countryCode')}
              value={element.countryCode}
              onChange={(event) => onUpdate({ countryCode: normalizeCountryCode(event.target.value) })}
              className="input w-full text-sm"
            >
              {COUNTRY_OPTIONS.map((country) => (
                <option key={country.code} value={country.code}>{country.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <FieldLabel htmlFor={fieldId('text')}>Treść opinii</FieldLabel>
          <textarea
            id={fieldId('text')}
            value={element.text}
            onChange={(event) => onUpdate({ text: event.target.value })}
            className="input w-full text-sm"
            rows={4}
            placeholder="Treść opinii"
          />
        </div>

        <div className="grid sm:grid-cols-4 gap-3">
          <NumberField id={fieldId('rating')} label="Ocena" value={element.rating} min={0} max={5} onChange={(rating) => onUpdate({ rating })} />
          <div>
            <FieldLabel htmlFor={fieldId('aggregateRating')}>Średnia</FieldLabel>
            <TextInput id={fieldId('aggregateRating')} value={element.aggregateRating} onChange={(aggregateRating) => onUpdate({ aggregateRating })} placeholder="4.8" />
          </div>
          <div>
            <FieldLabel htmlFor={fieldId('aggregateLabel')}>Opis średniej</FieldLabel>
            <TextInput id={fieldId('aggregateLabel')} value={element.aggregateLabel} onChange={(aggregateLabel) => onUpdate({ aggregateLabel })} placeholder="Znakomity" />
          </div>
          <div>
            <FieldLabel htmlFor={fieldId('reviewCount')}>Liczba opinii</FieldLabel>
            <TextInput id={fieldId('reviewCount')} value={element.reviewCount} onChange={(reviewCount) => onUpdate({ reviewCount })} placeholder="5,456" />
          </div>
        </div>

        <div className="rounded-xl border border-border-subtle bg-surface-base/40 p-3 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <AvatarPreview element={element} />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-text-primary">Avatar opinii</p>
              </div>
            </div>
            <select
              value={element.avatarType}
              onChange={(event) => onUpdate({ avatarType: event.target.value === 'custom' ? 'custom' : 'preset' })}
              className="input text-sm py-2"
            >
              <option value="preset">Predefiniowany</option>
              <option value="custom">Własny</option>
            </select>
          </div>

          {element.avatarType === 'preset' ? (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <FieldLabel htmlFor={fieldId('avatarPresetGender')}>Płeć</FieldLabel>
                  <select
                    id={fieldId('avatarPresetGender')}
                    value={element.avatarPresetGender}
                    onChange={(event) => onUpdate({ avatarPresetGender: normalizeCustomerAvatarGender(event.target.value) })}
                    className="input w-full text-sm"
                  >
                    <option value="female">Kobieta</option>
                    <option value="male">Mężczyzna</option>
                  </select>
                </div>
                <div>
                  <FieldLabel htmlFor={fieldId('avatarPresetAge')}>Wiek</FieldLabel>
                  <select
                    id={fieldId('avatarPresetAge')}
                    value={element.avatarPresetAge}
                    onChange={(event) => onUpdate({ avatarPresetAge: normalizeCustomerAvatarAge(event.target.value) })}
                    className="input w-full text-sm"
                  >
                    {CUSTOMER_AVATAR_AGES.map((age) => (
                      <option key={age} value={age}>{age}</option>
                    ))}
                  </select>
                </div>
              </div>
              <AvatarPresetPicker
                value={element.avatarPresetIndex}
                gender={element.avatarPresetGender}
                age={element.avatarPresetAge}
                onChange={(avatarPresetIndex) => onUpdate({ avatarPresetIndex })}
              />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => onPickImage('avatarCustom')}
                  disabled={!mediaPickerEnabled}
                  className="btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  title={mediaPickerEnabled ? undefined : 'Biblioteka mediów wymaga integracji sklepu'}
                >
                  {element.avatarCustom ? 'Zmień avatar' : 'Wybierz avatar'}
                </button>
                {element.avatarCustom && (
                  <button type="button" onClick={() => onRemoveImage('avatarCustom')} className="text-xs text-text-muted hover:text-danger">
                    Usuń avatar
                  </button>
                )}
              </div>
              <div>
                <FieldLabel htmlFor={fieldId('avatarCustom')}>URL avatara</FieldLabel>
                <TextInput
                  id={fieldId('avatarCustom')}
                  value={element.avatarCustom}
                  onChange={(avatarCustom) => onUpdate({ avatarCustom, avatarType: avatarCustom.trim() ? 'custom' : element.avatarType })}
                  placeholder="https://..."
                  type="url"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ElementSpacingField element={element} onUpdate={onUpdate} />
      <div className="rounded-xl border border-border-subtle bg-surface-base/40 p-3">
        {element.imageUrl ? (
          <img src={element.imageUrl} alt={element.alt} className="w-full max-h-56 object-contain rounded-lg bg-surface-hover" />
        ) : (
          <div className="h-40 flex items-center justify-center rounded-lg border border-dashed border-border-subtle text-text-muted">
            <ImageIcon className="w-8 h-8" />
          </div>
        )}
        <div className="flex items-center gap-3 mt-3">
          <button
            type="button"
            onClick={() => onPickImage('imageUrl')}
            disabled={!mediaPickerEnabled}
            className="btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            title={mediaPickerEnabled ? undefined : 'Biblioteka mediów wymaga integracji sklepu'}
          >
            {element.imageUrl ? 'Zmień grafikę' : 'Wybierz grafikę'}
          </button>
          {element.imageUrl && (
            <button type="button" onClick={() => onRemoveImage('imageUrl')} className="text-xs text-text-muted hover:text-danger">
              Usuń grafikę
            </button>
          )}
        </div>
      </div>
      <div>
        <FieldLabel htmlFor={fieldId('imageUrl')}>URL grafiki</FieldLabel>
        <TextInput id={fieldId('imageUrl')} value={element.imageUrl} onChange={(imageUrl) => onUpdate({ imageUrl })} placeholder="https://..." type="url" />
      </div>
      <div>
        <FieldLabel htmlFor={fieldId('alt')}>Alt tekst</FieldLabel>
        <TextInput id={fieldId('alt')} value={element.alt} onChange={(alt) => onUpdate({ alt })} placeholder="Opis grafiki" />
      </div>
      <div>
        <FieldLabel htmlFor={fieldId('linkUrl')}>Link po kliknięciu</FieldLabel>
        <TextInput id={fieldId('linkUrl')} value={element.linkUrl} onChange={(linkUrl) => onUpdate({ linkUrl })} placeholder="https://..." type="url" />
      </div>
    </div>
  );
}

export function ProductAdditionalElementsSection({
  integrationId,
  value,
  onChange,
  description = `Maksymalnie ${MAX_PRODUCT_ADDITIONAL_ELEMENTS} elementów na produkt.`,
  mediaPickerEnabled = Boolean(integrationId),
}: ProductAdditionalElementsSectionProps) {
  const elements = value.slice(0, MAX_PRODUCT_ADDITIONAL_ELEMENTS);
  const [selectedId, setSelectedId] = useState<string | null>(elements[0]?.id ?? null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [mediaTarget, setMediaTarget] = useState<{ elementId: string; field: 'imageUrl' | 'avatarCustom' } | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const selectedElement = useMemo(
    () => elements.find((element) => element.id === selectedId) ?? elements[0] ?? null,
    [elements, selectedId],
  );
  const isFull = elements.length >= MAX_PRODUCT_ADDITIONAL_ELEMENTS;

  const commitElements = (next: ProductAdditionalElement[]) => {
    onChange(normalizeProductAdditionalElements(next));
  };

  const addElement = (type: ProductAdditionalElementType) => {
    if (isFull) return;
    const element = {
      ...createDefaultElement(type),
      marginTop: elements.length === 0 ? 15 : 10,
    };
    commitElements([...elements, element]);
    setSelectedId(element.id);
  };

  const updateElement = (id: string, patch: ProductAdditionalElementPatch) => {
    commitElements(elements.map((element) => (
      element.id === id ? ({ ...element, ...patch } as ProductAdditionalElement) : element
    )));
  };

  const removeElement = (id: string) => {
    const index = elements.findIndex((element) => element.id === id);
    const next = elements.filter((element) => element.id !== id);
    commitElements(next);
    if (selectedId === id) {
      setSelectedId(next[index]?.id ?? next[index - 1]?.id ?? null);
    }
  };

  const duplicateElement = (id: string) => {
    if (isFull) return;
    const index = elements.findIndex((element) => element.id === id);
    if (index < 0) return;

    const duplicate = normalizeAdditionalElement({
      ...elements[index],
      id: createElementId(),
      marginTop: Math.max(elements[index].marginTop, 10),
    });
    if (!duplicate) return;

    const next = [...elements];
    next.splice(index + 1, 0, duplicate);
    commitElements(next);
    setSelectedId(duplicate.id);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over) return;

    const source = active.data.current?.source;
    const type = active.data.current?.type as ProductAdditionalElementType | undefined;
    const overId = String(over.id);
    const overCanvas = overId === CANVAS_ID || elements.some((element) => element.id === overId);

    if (source === 'palette' && type && overCanvas) {
      addElement(type);
      return;
    }

    if (source === 'canvas' && active.id !== over.id) {
      const oldIndex = elements.findIndex((element) => element.id === active.id);
      const newIndex = elements.findIndex((element) => element.id === over.id);
      if (oldIndex >= 0 && newIndex >= 0) {
        commitElements(arrayMove(elements, oldIndex, newIndex));
      }
    }
  };

  const activePaletteItem = PALETTE.find((item) => `palette-${item.type}` === activeDragId);
  const ActivePaletteIcon = activePaletteItem?.icon;
  const activeElement = elements.find((element) => element.id === activeDragId);

  const handleMediaSelect = (url: string, item?: WpMediaItem) => {
    if (!mediaTarget) return;
    const target = elements.find((element) => element.id === mediaTarget.elementId);
    if (!target) return;

    if (target.type === 'image' && mediaTarget.field === 'imageUrl') {
      updateElement(target.id, {
        imageUrl: url,
        alt: item?.alt || item?.name || target.alt,
      });
    }

    if (target.type === 'review' && mediaTarget.field === 'avatarCustom') {
      updateElement(target.id, { avatarCustom: url, avatarType: 'custom' });
    }

    setMediaTarget(null);
  };

  const handleRemoveImage = (field: 'imageUrl' | 'avatarCustom') => {
    if (!selectedElement) return;
    if (selectedElement.type === 'image' && field === 'imageUrl') {
      updateElement(selectedElement.id, { imageUrl: '' });
    }
    if (selectedElement.type === 'review' && field === 'avatarCustom') {
      updateElement(selectedElement.id, { avatarCustom: '' });
    }
  };

  return (
    <div className="rounded-xl border border-border bg-surface-raised/30 p-4 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-text-primary">Elementy pod CTA</p>
          <p className="text-xs text-text-muted mt-1">{description}</p>
        </div>
        <span className={`text-xs font-medium ${isFull ? 'text-warning' : 'text-text-muted'}`}>
          {elements.length}/{MAX_PRODUCT_ADDITIONAL_ELEMENTS}
        </span>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveDragId(null)}
      >
        <div className="grid gap-4 lg:grid-cols-[230px_minmax(0,1fr)]">
          <div className="space-y-2">
            {PALETTE.map((item) => (
              <PaletteItem key={item.type} item={item} disabled={isFull} onAdd={() => addElement(item.type)} />
            ))}
          </div>

          <CanvasDropZone isEmpty={elements.length === 0} isFull={isFull} isDragActive={Boolean(activeDragId)}>
            <SortableContext items={elements.map((element) => element.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-3">
                {elements.map((element) => (
                  <SortableElementCard
                    key={element.id}
                    element={element}
                    selected={selectedElement?.id === element.id}
                    canDuplicate={!isFull}
                    onSelect={() => setSelectedId(element.id)}
                    onDuplicate={() => duplicateElement(element.id)}
                    onRemove={() => removeElement(element.id)}
                  />
                ))}
              </div>
            </SortableContext>
          </CanvasDropZone>
        </div>

        <DragOverlay dropAnimation={null}>
          {activePaletteItem && ActivePaletteIcon ? (
            <div className="w-56 rounded-xl border border-accent-primary bg-surface-raised p-3 shadow-lg">
              <div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
                <ActivePaletteIcon className="w-4 h-4 text-accent-primary" />
                {activePaletteItem.label}
              </div>
            </div>
          ) : activeElement ? (
            <div className="w-72 rounded-xl border border-accent-primary bg-surface-raised p-3 shadow-lg">
              <AdditionalElementPreview element={activeElement} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {selectedElement ? (
        <div className="rounded-xl border border-border-subtle bg-surface-base/40 p-4">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <p className="text-sm font-semibold text-text-primary">{getElementTitle(selectedElement)}</p>
              <p className="text-xs text-text-muted mt-0.5">Konfiguracja wybranego elementu</p>
            </div>
          </div>
          <ElementConfigPanel
            element={selectedElement}
            onUpdate={(patch) => updateElement(selectedElement.id, patch)}
            onPickImage={(field) => setMediaTarget({ elementId: selectedElement.id, field })}
            onRemoveImage={handleRemoveImage}
            mediaPickerEnabled={mediaPickerEnabled}
          />
        </div>
      ) : (
        <div className="rounded-xl border border-border-subtle bg-surface-base/40 p-4 text-sm text-text-muted">
          Kliknij element na canvasie, aby go skonfigurować.
        </div>
      )}

      {mediaTarget && mediaPickerEnabled && integrationId && (
        <WpMediaPickerModal
          integrationId={integrationId}
          onSelect={handleMediaSelect}
          onClose={() => setMediaTarget(null)}
        />
      )}
    </div>
  );
}
